import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import { useUpdateProgress } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { FC, useEffect, useRef } from 'react';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';

import { GcsUploadPage } from '#/features/community-hub/components/gcs-upload/gcs-upload-page';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

import { useResolvedBlock } from './use-resolved-block';

type TGcsUploadItemViewProps = {
  /** URL slug of the content block. */
  itemSlug: string;
};

/** GCS upload content block viewer. Shared by the program-level and lesson-level routes. */
export const GcsUploadItemView: FC<TGcsUploadItemViewProps> = ({ itemSlug }) => {
  const navigate = useNavigate();

  const {
    programId,
    moduleId,
    lessonId,
    programSlug,
    moduleSlug,
    lessonSlug,
    lessonBasePath,
    lesson,
    module,
    findParentLesson,
    block,
    isLoading: isLoadingLesson,
    error: lessonError,
  } = useResolvedBlock(itemSlug, ContentBlockType.GCS_UPLOAD);

  const backToLessonUrl = lessonBasePath;

  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  useEffect(() => {
    if (!trackedRef.current && block) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: block.id });
    }
  }, [block, updateProgress]);

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="error">Upload block not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!block.gcsBaseUrl) {
    return (
      <Box p={3}>
        <Alert severity="info">This upload block is not fully configured yet.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  return (
    <>
      <LessonBreadcrumbs
        spaceId={programId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel={block.title || 'Upload'}
        programSlug={programSlug}
        moduleSlug={moduleSlug}
        lessonSlug={lessonSlug}
      />
      <GcsUploadPage block={block} lessonId={lessonId} />
    </>
  );
};
